import React, { useEffect, useRef, useState } from "react";
import { AssistantMessageFrame } from "./AssistantMessageFrame";
import { messageTimestamp } from "./messageTimestamp";
import type { AgenticSearchProgress } from "./types";
import { useTranslation } from "react-i18next";
import styled, { keyframes } from "styled-components/macro";

const breathe = keyframes`
  0%, 100% { opacity: 0.35; }
  50% { opacity: 1; }
`;

const Activity = styled(AssistantMessageFrame)`
  color: ${({ theme }) => theme.primaryTextColor};
  overflow-wrap: anywhere;
`;

const StatusRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  color: ${({ theme }) => theme.secondaryTextColor};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
`;

const Dot = styled.span<{ $stopping: boolean }>`
  position: absolute;
  left: -14px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
  animation: ${({ $stopping }) => ($stopping ? "none" : breathe)} 1.6s
    ease-in-out infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Elapsed = styled.span`
  font-variant-numeric: tabular-nums;
`;

const Plan = styled.p`
  margin: 10px 0 0;
  white-space: pre-wrap;
`;

const Trail = styled.details`
  margin-top: ${({ theme }) => theme.resolve("spacing_lg")};
  color: ${({ theme }) => theme.secondaryTextColor};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  summary {
    cursor: pointer;
    width: fit-content;
    position: relative;
    list-style: none;
    &::-webkit-details-marker {
      display: none;
    }
    &::before {
      content: "▸";
      position: absolute;
      left: -14px;
    }
  }
  &[open] summary::before {
    content: "▾";
  }
  ol {
    list-style: none;
    padding: 0;
    margin: ${({ theme }) => theme.resolve("spacing_md")} 0 0;
  }
  li {
    display: flex;
    justify-content: space-between;
    gap: ${({ theme }) => theme.resolve("spacing_lg")};
    padding: 3px 0;
  }
`;

const Reassurance = styled.p`
  margin: ${({ theme }) => theme.resolve("spacing_xl")} 0 0;
  color: ${({ theme }) => theme.secondaryTextColor};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
`;

type ActivityEntry = { phase: AgenticSearchProgress["phase"]; seconds: number };

export function PendingAssistantBubble({
  progress,
  stopping = false,
  createdAt,
}: {
  progress?: AgenticSearchProgress | null;
  stopping?: boolean;
  createdAt?: string;
}) {
  const { t } = useTranslation();
  const mountedAt = useRef(Date.now());
  const parsedStart = createdAt ? Date.parse(createdAt) : NaN;
  const startedAt = Number.isFinite(parsedStart)
    ? parsedStart
    : mountedAt.current;
  const elapsedSeconds = () =>
    Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  const [seconds, setSeconds] = useState(elapsedSeconds);
  const [history, setHistory] = useState<ActivityEntry[]>([]);
  const phases: Record<AgenticSearchProgress["phase"], string> = {
    working: t("Working on your search…"),
    searching_records: t("Checking records…"),
    reading_documents: t("Reading supporting documents…"),
    finalizing: t("Preparing the answer…"),
  };

  useEffect(() => {
    const update = () =>
      setSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    update();
    const interval = window.setInterval(update, 1000);
    return () => window.clearInterval(interval);
  }, [startedAt]);

  useEffect(() => {
    if (!progress || stopping) return;
    const phase = progress.phase;
    const updated = Date.parse(progress.updated_at);
    const observedSeconds = Math.max(
      0,
      Math.floor(
        ((Number.isFinite(updated) ? updated : Date.now()) - startedAt) / 1000
      )
    );
    setHistory((previous) =>
      previous[previous.length - 1]?.phase === phase
        ? previous
        : [...previous, { phase, seconds: observedSeconds }]
    );
  }, [progress, startedAt, stopping]);

  return (
    <Activity aria-busy="true" title={messageTimestamp(createdAt)}>
      <StatusRow>
        <Dot $stopping={stopping} aria-hidden="true" />
        <span role="status">
          {stopping
            ? t("Stopping…")
            : (progress && phases[progress.phase]) ||
              t("Preparing your search…")}
        </span>
        <Elapsed aria-label={t("Elapsed time")} aria-live="off">
          {seconds}s
        </Elapsed>
      </StatusRow>
      {!stopping && progress?.plan && <Plan>{progress.plan}</Plan>}
      {!stopping && history.length > 0 && (
        <Trail>
          <summary>{t("Search activity")}</summary>
          <ol aria-label={t("Observed search activity")}>
            {history.map((entry, index) => (
              <li key={index}>
                <span>{phases[entry.phase]}</span>
                <Elapsed>{entry.seconds}s</Elapsed>
              </li>
            ))}
          </ol>
        </Trail>
      )}
      {!stopping && seconds >= 30 && (
        <Reassurance>
          {t("Still working on this search. You can stop it at any time.")}
        </Reassurance>
      )}
    </Activity>
  );
}
